import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Download, CreditCard, Scan } from 'lucide-react';
import { getAuthHeaders, getUser } from '../lib/auth';
import BusinessCardScanner from '../components/BusinessCardScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileEdit = () => {
  const navigate = useNavigate();
  const currentUser = getUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    businessName: '',
    businessCategory: '',
    address: '',
    zipcode: '',
    profilePicUrl: '',
    logoUrl: '',
    about: ''
  });
  const [socialLinks, setSocialLinks] = useState([]);
  const [newLink, setNewLink] = useState({ platform: '', url: '' });

  useEffect(() => {
    loadProfile();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/business-categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fixImageUrl = (url) => {
    if (!url) return url;
    
    // If URL is from ANY old preview domain, replace with current backend URL
    if (url.includes('.preview.emergentagent.com/api/')) {
      const path = url.split('/api/')[1];
      if (path) {
        return `${process.env.REACT_APP_BACKEND_URL}/api/${path}`;
      }
    }
    
    // If URL is relative, prepend backend URL
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    
    return url;
  };

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders()
      });
      const user = response.data;
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        email: user.email || '',
        businessName: user.businessName || '',
        businessCategory: user.businessCategory || '',
        address: user.address || '',
        zipcode: user.zipcode || '',
        profilePicUrl: fixImageUrl(user.profilePicUrl) || '',
        logoUrl: fixImageUrl(user.logoUrl) || '',
        about: user.about || ''
      });
      setSocialLinks(user.socialLinks || []);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Profile updated successfully!');
      navigate(`/profile/${currentUser.id}`);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const generateMyVCard = () => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${formData.name || 'Unknown'}`,
      formData.businessName ? `ORG:${formData.businessName}` : '',
      currentUser.email ? `EMAIL:${currentUser.email}` : '',
      formData.address ? `ADR;TYPE=WORK:;;${formData.address}${formData.zipcode ? ', ' + formData.zipcode : ''}` : '',
      ...socialLinks.map(link => link.url ? `URL:${link.url}` : '').filter(Boolean),
      'END:VCARD'
    ].filter(line => line && !line.endsWith(':')).join('\n');
    
    return vcard;
  };

  const handleSaveAndDownloadVCard = () => {
    try {
      // Generate vCard
      const vcard = generateMyVCard();
      
      // Download vCard
      const blob = new Blob([vcard], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formData.name?.replace(/\s+/g, '_') || 'my-card'}.vcf`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      // Save to My Connections
      const existingContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      
      // Check if already exists
      const existingIndex = existingContacts.findIndex(c => 
        c.email === currentUser.email || (c.name === formData.name && c.source === 'my-card')
      );
      
      const myContact = {
        id: existingIndex >= 0 ? existingContacts[existingIndex].id : Date.now().toString(),
        name: formData.name,
        businessName: formData.businessName || '',
        title: '',
        email: currentUser.email || '',
        phone: '',
        mobile: '',
        website: socialLinks.find(l => l.platform.toLowerCase().includes('website'))?.url || '',
        address: formData.address + (formData.zipcode ? ', ' + formData.zipcode : ''),
        linkedin: socialLinks.find(l => l.platform.toLowerCase().includes('linkedin'))?.url || '',
        scannedAt: new Date().toISOString(),
        source: 'my-card',
      };
      
      if (existingIndex >= 0) {
        // Update existing
        existingContacts[existingIndex] = myContact;
      } else {
        // Add new
        existingContacts.unshift(myContact);
      }
      
      const trimmedContacts = existingContacts.slice(0, 100);
      localStorage.setItem('scannedContacts', JSON.stringify(trimmedContacts));
      
      toast.success('✅ vCard downloaded & saved to My Connections!');
    } catch (error) {
      console.error('vCard error:', error);
      toast.error('Failed to generate vCard');
    }
  };

  const handleAddLink = async () => {
    if (!newLink.platform || !newLink.url) {
      toast.error('Please fill in both platform and URL');
      return;
    }
    try {
      await axios.post(`${API}/users/me/social-links`, newLink, {
        headers: getAuthHeaders()
      });
      setSocialLinks([...socialLinks, newLink]);
      setNewLink({ platform: '', url: '' });
      toast.success('Social link added');
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const handleRemoveLink = async (index) => {
    try {
      await axios.delete(`${API}/users/me/social-links/${index}`, {
        headers: getAuthHeaders()
      });
      setSocialLinks(socialLinks.filter((_, i) => i !== index));
      toast.success('Link removed');
    } catch (error) {
      toast.error('Failed to remove link');
    }
  };

  const handleCardScanned = (scannedData) => {
    // Auto-populate form with scanned data
    setFormData(prev => ({
      ...prev,
      name: scannedData.name || prev.name,
      phone: scannedData.phone || prev.phone,
      businessName: scannedData.company || prev.businessName,
      address: scannedData.address || prev.address,
      about: scannedData.title ? `${scannedData.title}${prev.about ? ' - ' + prev.about : ''}` : prev.about
    }));
    
    // If website is in scanned data, add it to social links
    if (scannedData.website) {
      const websiteExists = socialLinks.some(link => link.url === scannedData.website);
      if (!websiteExists) {
        setSocialLinks(prev => [...prev, { platform: 'Website', url: scannedData.website }]);
      }
    }
    
    toast.success('Business card data imported! Review and save your profile.');
    setShowScanner(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate(`/profile/${currentUser.id}`)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Button>

        <Card className="p-8 bg-white shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Edit Profile
            </h1>
            <Button
              type="button"
              onClick={() => setShowScanner(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              <Scan className="w-4 h-4 mr-2" />
              Scan My Card
            </Button>
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-900 font-semibold">Quick Setup</p>
                <p className="text-sm text-blue-700">
                  Have a physical business card? Click "Scan My Card" to automatically fill your profile with your card details.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSaveProfile} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessCategory">Business Category</Label>
              <Select
                value={formData.businessCategory}
                onValueChange={(value) => setFormData({ ...formData, businessCategory: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Business Address or PO Box</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, City, State or PO Box 123"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipcode">ZIP Code</Label>
              <Input
                id="zipcode"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                placeholder="12345"
                maxLength={5}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profilePicUrl">Profile Picture</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const uploadFormData = new FormData();
                      uploadFormData.append('file', file);
                      const toastId = toast.loading('Uploading profile picture...');
                      try {
                        const response = await axios.post(`${API}/images/upload`, uploadFormData, {
                          headers: {
                            ...getAuthHeaders(),
                            'Content-Type': 'multipart/form-data'
                          }
                        });
                        // Use relative URL if response includes /api, otherwise prepend backend URL
                        const imageUrl = response.data.url.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`
                          : response.data.url;
                        setFormData(prev => ({ ...prev, profilePicUrl: imageUrl }));
                        toast.success('Profile picture uploaded successfully!', { id: toastId });
                      } catch (error) {
                        console.error('Upload error:', error);
                        toast.error('Failed to upload profile picture', { id: toastId });
                      }
                    }
                  }}
                  className="flex-1"
                />
              </div>
              {formData.profilePicUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Preview:</p>
                  <img 
                    src={formData.profilePicUrl} 
                    alt="Profile preview" 
                    className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 shadow-md" 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      toast.error('Failed to load image. Please check the URL.');
                    }}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="logoUrl">Logo URL</Label>
              <Input
                id="logoUrl"
                name="logoUrl"
                value={formData.logoUrl}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="about">About</Label>
              <Textarea
                id="about"
                name="about"
                value={formData.about}
                onChange={handleChange}
                placeholder="Tell us about yourself or your business..."
                rows={4}
              />
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 text-lg"
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
              
              <Button
                type="button"
                onClick={handleSaveAndDownloadVCard}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full py-6 text-lg font-bold shadow-lg"
              >
                <Download className="w-5 h-5 mr-2" />
                Save & Download My vCard
              </Button>
              
              <p className="text-xs text-center text-slate-500 mt-2">
                💾 Downloads your vCard and adds it to My Connections automatically
              </p>
            </div>
          </form>

          {/* Social Links Section */}
          <div className="mt-8 pt-8 border-t border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Social Links
            </h2>

            {socialLinks.length > 0 && (
              <div className="space-y-3 mb-6">
                {socialLinks.map((link, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{link.platform}</p>
                      <p className="text-sm text-slate-600 truncate">{link.url}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveLink(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  placeholder="Platform (e.g., LinkedIn)"
                  value={newLink.platform}
                  onChange={(e) => setNewLink({ ...newLink, platform: e.target.value })}
                />
                <Input
                  placeholder="URL"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                />
              </div>
              <Button
                type="button"
                onClick={handleAddLink}
                variant="outline"
                className="w-full rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Social Link
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Business Card Scanner Modal */}
      <BusinessCardScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onSave={handleCardScanned}
        skipDownload={true}
      />
    </div>
  );
};

export default ProfileEdit;
